<template>
  <div :class="$style.emptyPageWrapper">
    <EmptyPageContent
      :loading="loading"
      :text="{
        title: $t('rebook_reminder.empty_state.title'),
        description: $t('rebook_reminder.empty_state.description'),
        btnPrimary: $t('rebook_reminder.empty_state.enable')
      }"
      :moreLink="{
        url: helpCenterArticleUrl(4582588),
        text: $t('rebook_reminder.empty_state.more_info')
      }"
      @click="enableTemplate"
      v-test="'rebook-reminder-empty-state'"
    >
      <template #visual>
        <RebookDisabledVisual />
      </template>
    </EmptyPageContent>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { useMutation } from '@vue/apollo-composable';
import { useRouter } from 'vue-router';
import EmptyPageContent from '@/components/EmptyPageContent.vue';
import eventBus from '@/event-bus';
import { helpCenterArticleUrl } from '@/helpers/help-center';
import RebookDisabledVisual from '@/modules/marketing/email/templates/rebook-reminder/RebookDisabledVisual.vue';
import { useCompanyStore } from '@/stores/company';
import { useServicesStore } from '@/stores/services';
import type { Service, ServiceCategory } from '@/types';
import { useMarketingBuilderStore } from '../../store/builder';

const { company } = useCompanyStore();
const { currentOnboardingFlow } = storeToRefs(useOnboardingStore());
const builderStore = useMarketingBuilderStore();
const loading = ref(false);

import { flash } from '@/helpers/ui';

const { updateCompany } = useCompanyStore();

import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useOnboardingStore } from '@/stores/onboarding';
import { UPDATE_SERVICES } from '../../modals/rebook-setup/graphql';

const { t } = useI18n();
const router = useRouter();

const enableTemplate = () => {
  loading.value = true;

  const { rebookEnabledServicesCount } = useServicesStore();
  if (rebookEnabledServicesCount === 0) {
    setAllServicesEnabled();
    router.push({ name: 'default-rebook-reminder-services' });
  } else {
    enableSetting();
  }
};

const enableSetting = (redirectToDashboard = true) => {
  const input = {
    settings: {
      bookings: {
        rebookReminderEnabled: true,
      },
    },
  };

  updateCompany(input).then(() => {
    builderStore.setEnabled(true);
    if (redirectToDashboard) {
      router.push({ name: 'rebook-reminder-dashboard' });
    }
    flash(t('global.flash.rebook_reminder_enabled'));

    if (currentOnboardingFlow.value === 'marketing') {
      eventBus.$emit('open-task-menu');
      currentOnboardingFlow.value = '';
    }
    loading.value = false;
  });
};

const { mutate } = useMutation(UPDATE_SERVICES);
const setAllServicesEnabled = () => {
  const { categoriesWithServices } = useServicesStore();
  const bulkUpdateServiceAttributes = categoriesWithServices.flatMap(
    (category: ServiceCategory) =>
      category.services
        ? category.services.map((service: Service) => ({
            id: service.id,
            rebookEnabled: true,
            rebookPeriod: 30,
          }))
        : [],
  );

  mutate({
    input: {
      bulkUpdateServiceAttributes,
    },
  }).then(() => {
    const { getServices } = useServicesStore();
    getServices();
    flash(t('global.flash.services_updated'));
    enableSetting(false);
  });
};
</script>

<style
  lang="scss"
  module
>
.emptyPageWrapper {
  padding: 0.5 * $spacing;
}
</style>
